import React, { useState, useEffect } from "react";
import { supabase } from "../../supabaseClient";
import { Tooltip } from "react-tooltip";
import { InformationCircleIcon } from "@heroicons/react/24/solid";
import { useTranslation } from "react-i18next";

const ImageUploader = ({
  initialImageUrl,
  onTempImageChange,
  bucket = "makeadream-images",
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const { t, i18n } = useTranslation();
  const [tempImageUrl, setTempImageUrl] = useState(initialImageUrl); // Image temporaire

  useEffect(() => {
    // Met à jour l'image temporaire si l'image initiale change
    setTempImageUrl(initialImageUrl);
  }, [initialImageUrl]);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      setError(null);

      const sanitizedFileName = sanitizeFileName(file.name);
      const fileName = `${sanitizedFileName}-${Date.now()}`;

      // Téléchargement dans Supabase Storage
      const { data, error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(fileName, file);

      if (uploadError) {
        throw uploadError;
      }

      // Obtenir l'URL publique
      const {
        data: { publicUrl },
      } = supabase.storage.from(bucket).getPublicUrl(data.path);

      setTempImageUrl(publicUrl); // Met à jour uniquement l'image temporaire

      // Notifie le parent de l'image temporaire
      onTempImageChange(publicUrl);
    } catch (error) {
      console.error("Erreur lors du téléchargement :", error.message);
      setError("Échec du téléchargement de l'image.");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = () => {
    setTempImageUrl(""); // Réinitialise uniquement l'image temporaire
    if (onTempImageChange) {
      onTempImageChange(""); // Notifie le parent de la suppression de l'image
    }
  };

  const sanitizeFileName = (name) => {
    return name
      .toLowerCase()
      .replace(/\s+/g, "-") // espaces → tirets
      .replace(/[^a-z0-9\-_.]/g, "") // supprime caractères spéciaux
      .replace(/-+/g, "-") // remplace plusieurs tirets consécutifs
      .replace(/^-+|-+$/g, ""); // supprime tirets début/fin
  };

  return (
    <div className="space-y-4" shadow>
      {tempImageUrl ? (
        <div className="relative w-full max-w-sm mx-auto">
          <img
            src={tempImageUrl}
            alt="Image téléchargée"
            className="w-full h-auto rounded-lg shadow-md"
            loading="lazy"
          />
          <div className="flex justify-center mt-2 space-x-2">
            <button
              onClick={handleDelete}
              className="bg-red-500 text-white px-4 py-2 rounded-full hover:bg-red-600 transition"
            >
              Supprimer l'image
            </button>
          </div>
        </div>
      ) : (
        <label
          htmlFor="image-upload"
          className="cursor-pointer flex items-center justify-center w-full h-32 border-dashed border-2 border-gray-300 rounded-lg hover:border-primary bg-gray-50 transition"
        >
          {uploading ? (
            <div className="flex items-center space-x-2">
              <span className="loader" />
              <span className="text-gray-600">Téléchargement en cours...</span>
            </div>
          ) : (
            <span className="text-gray-600 flex">
              <InformationCircleIcon
                data-tooltip-id="image-info"
                className="w-5 h-5 mr-2 text-muted cursor-pointer"
              />
              <Tooltip
                id="image-info"
                place="right"
                className="z-50 max-w-xs text-sm bg-primary text-white px-3 py-2 rounded-lg"
              >
                <div>
                  Taille idéale : 600×400px
                  <br />
                  Taille minimale : 300×200px
                  <br />
                  Formats : JPG, PNG, WebP
                </div>
              </Tooltip>
              Cliquez ou glissez une image ici
            </span>
          )}
        </label>
      )}
      <input
        id="image-upload"
        type="file"
        accept="image/*"
        onChange={handleFileUpload}
        className="hidden"
      />
      {error && <p className="text-red-500">{error}</p>}

      <style>
        {`
          .loader {
            border: 4px solid #f3f3f3;
            border-top: 4px solid #3498db;
            border-radius: 50%;
            width: 16px;
            height: 16px;
            animation: spin 1s linear infinite;
          }

          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default ImageUploader;
